/**
 * SettlementStatementDialog
 *
 * Structured HUD/Closing-Disclosure entry. Solves the long-standing problem
 * of operators stuffing "cash actually wired" into Project.purchase_price.
 *
 * Behavior:
 *  - User enters the *gross contract price* (matches the deed).
 *  - EMD applied at closing is tracked separately (offsets cash wired but is
 *    already part of cost basis under category "Earnest Money Deposit").
 *  - Each HUD line becomes a structured row: label + amount + category +
 *    kind (charge / proration / credit). Category is auto-suggested via the
 *    backend `/settlement-statements/suggest-category` keyword matcher and
 *    can be overridden from a dropdown.
 *  - Live preview of "Cash at Closing" = gross − EMD − credits + charges +
 *    prorations.
 *  - On Save the backend writes the whole atomic set of ledger transactions
 *    (replacing any prior settlement-linked txns) and syncs
 *    `project.purchase_price` (buy) or `project.sale_price` (sell) to the
 *    gross figure.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Trash2, FileSpreadsheet, Wand2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Separator } from './ui/separator';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { settlementApi } from '../lib/api';
import { TRANSACTION_CATEGORY_GROUPS } from '../lib/utils';

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Canonical category dropdown options, scoped to the buckets relevant to a
// settlement: closing/dd/improvements/selling. (purchase bucket only via
// the dedicated gross-price field — we never want a user picking "Purchase
// Price" as a line-item category.)
const settlementCategoriesByBucket = TRANSACTION_CATEGORY_GROUPS
  .filter(g => ['closing', 'dd', 'improvements', 'selling'].includes(g.bucket));

const emptyLine = (overrides = {}) => ({
  label: '',
  amount: 0,
  category: 'Other Closing Cost',
  kind: 'charge',
  ...overrides,
});

export default function SettlementStatementDialog({ open, onOpenChange, project, side = 'buy', onSaved }) {
  const [statementId, setStatementId] = useState(null);
  const [statementDate, setStatementDate] = useState('');
  const [grossPrice, setGrossPrice] = useState(0);
  const [emdApplied, setEmdApplied] = useState(0);
  const [lineItems, setLineItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Track which line labels we've already suggested a category for, so we
  // don't clobber the user's manual override on every keystroke.
  const suggestedLabelsRef = useRef({});

  // ── Load existing statement when dialog opens ─────────────────────────
  useEffect(() => {
    if (!open || !project?.id) return;
    let cancelled = false;
    setLoading(true);
    settlementApi.get(project.id, side)
      .then(res => {
        if (cancelled) return;
        const s = res.data;
        if (s) {
          setStatementId(s.id);
          setStatementDate(s.statement_date || '');
          setGrossPrice(s.gross_price || 0);
          setEmdApplied(s.emd_applied || 0);
          setLineItems(s.line_items?.length ? s.line_items : []);
          setNotes(s.notes || '');
          // Re-mark every loaded label as already-suggested so we don't
          // auto-override the operator's prior category choices.
          const seen = {};
          (s.line_items || []).forEach(li => { seen[li.label?.trim().toLowerCase()] = true; });
          suggestedLabelsRef.current = seen;
        } else {
          // Fresh statement — prefill from existing project data where useful
          setStatementId(null);
          setStatementDate(side === 'buy' ? (project.acquisition_date || '') : (project.sale_date || ''));
          setGrossPrice(side === 'buy' ? (project.purchase_price || 0) : (project.sale_price || 0));
          setEmdApplied(0);
          setLineItems([]);
          setNotes('');
          suggestedLabelsRef.current = {};
        }
      })
      .catch(() => toast.error('Could not load settlement statement'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, project?.id, side]);

  // ── Live "Cash at Closing" preview ───────────────────────────────────
  // Buy side:  cash_wired = gross − EMD + charges + prorations − credits
  // Sell side: cash_received = gross + credits − charges − prorations
  // (Prorations and credits stored as positive numbers; their effect is
  // determined by their `kind` flag.)
  const cashAtClosing = useMemo(() => {
    const charges = lineItems.filter(l => l.kind === 'charge').reduce((s, l) => s + Number(l.amount || 0), 0);
    const prorations = lineItems.filter(l => l.kind === 'proration').reduce((s, l) => s + Number(l.amount || 0), 0);
    const credits = lineItems.filter(l => l.kind === 'credit').reduce((s, l) => s + Number(l.amount || 0), 0);
    if (side === 'buy') {
      return Number(grossPrice || 0) - Number(emdApplied || 0) + charges + prorations - credits;
    }
    return Number(grossPrice || 0) + credits - charges - prorations;
  }, [grossPrice, emdApplied, lineItems, side]);

  // ── Auto-suggest category as user types the label ────────────────────
  const onLabelBlur = async (idx, label) => {
    if (!label?.trim()) return;
    const key = label.trim().toLowerCase();
    // Only suggest once per unique label, never override a manual pick.
    if (suggestedLabelsRef.current[key]) return;
    try {
      const res = await settlementApi.suggestCategory(label, side);
      const suggested = res.data?.category;
      if (suggested) {
        setLineItems(prev => {
          const next = [...prev];
          if (next[idx]) next[idx] = { ...next[idx], category: suggested };
          return next;
        });
      }
      suggestedLabelsRef.current[key] = true;
    } catch {
      // Silent — operator still has the override dropdown.
    }
  };

  // ── Row mutation helpers ─────────────────────────────────────────────
  const updateLine = (idx, patch) => {
    setLineItems(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };
  const addLine = (kind = 'charge') => setLineItems(prev => [...prev, emptyLine({ kind, category: side === 'buy' ? 'Other Closing Cost' : 'Other Selling Cost' })]);
  const removeLine = (idx) => setLineItems(prev => prev.filter((_, i) => i !== idx));

  // ── Save ──────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!grossPrice || grossPrice <= 0) {
      toast.error('Enter a gross contract price first');
      return;
    }
    setSaving(true);
    try {
      const res = await settlementApi.save(project.id, {
        side,
        statement_date: statementDate || null,
        gross_price: Number(grossPrice),
        emd_applied: Number(emdApplied || 0),
        line_items: lineItems.map(l => ({
          label: l.label || '',
          amount: Number(l.amount || 0),
          category: l.category,
          kind: l.kind,
        })),
        notes: notes || null,
      });
      toast.success(`Settlement saved · ${res.data.txns_written} ledger entries written`);
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settlement statement');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete (only when statement exists) ──────────────────────────────
  const handleDelete = async () => {
    if (!statementId) return;
    if (!window.confirm('Delete this settlement statement and all associated ledger transactions?')) return;
    setSaving(true);
    try {
      await settlementApi.delete(project.id, statementId);
      toast.success('Settlement statement deleted');
      onSaved?.(null);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="settlement-statement-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Settlement Statement — {side === 'buy' ? 'Purchase' : 'Sale'} · {project?.name}
            {statementId && <Badge variant="outline" className="ml-2 text-[10px]">Editing existing</Badge>}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="space-y-5">

            {/* Headline */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="gross-price" className="text-xs">
                  {side === 'buy' ? 'Contract Sales Price (Gross)' : 'Sale Price (Gross)'}
                </Label>
                <Input
                  id="gross-price"
                  type="number"
                  step="0.01"
                  value={grossPrice}
                  onChange={e => setGrossPrice(e.target.value)}
                  data-testid="settlement-gross-price"
                />
                <p className="text-[11px] text-muted-foreground mt-1">Matches the deed. This is the headline price.</p>
              </div>
              {side === 'buy' && (
                <div>
                  <Label htmlFor="emd-applied" className="text-xs">Earnest Money Applied</Label>
                  <Input
                    id="emd-applied"
                    type="number"
                    step="0.01"
                    value={emdApplied}
                    onChange={e => setEmdApplied(e.target.value)}
                    data-testid="settlement-emd"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">Deposit already paid; credited at closing.</p>
                </div>
              )}
              <div>
                <Label htmlFor="statement-date" className="text-xs">Statement Date</Label>
                <Input
                  id="statement-date"
                  type="date"
                  value={statementDate}
                  onChange={e => setStatementDate(e.target.value)}
                  data-testid="settlement-date"
                />
              </div>
            </div>

            <Separator />

            {/* Line items table */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold">HUD Line Items</h4>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => addLine('charge')} data-testid="add-charge-row" className="gap-1.5">
                    <Plus className="w-3 h-3" /> Charge
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => addLine('proration')} data-testid="add-proration-row" className="gap-1.5">
                    <Plus className="w-3 h-3" /> Proration
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => addLine('credit')} data-testid="add-credit-row" className="gap-1.5">
                    <Plus className="w-3 h-3" /> Credit
                  </Button>
                </div>
              </div>

              {lineItems.length === 0 ? (
                <p className="text-sm text-muted-foreground italic py-6 text-center border border-dashed rounded-sm">
                  No line items yet. Add each HUD/CD line above — title fees, recording, attorney, prorations, etc.
                </p>
              ) : (
                <div className="space-y-2">
                  {lineItems.map((line, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-start" data-testid={`settlement-line-${idx}`}>
                      <div className="col-span-5">
                        <Input
                          placeholder="Description (e.g. Settlement fee to The Humphries Law Firm)"
                          value={line.label}
                          onChange={e => updateLine(idx, { label: e.target.value })}
                          onBlur={e => onLabelBlur(idx, e.target.value)}
                          data-testid={`settlement-line-label-${idx}`}
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={line.amount}
                          onChange={e => updateLine(idx, { amount: e.target.value })}
                          className="font-mono text-right"
                          data-testid={`settlement-line-amount-${idx}`}
                        />
                      </div>
                      <div className="col-span-3">
                        <Select value={line.category} onValueChange={v => updateLine(idx, { category: v })}>
                          <SelectTrigger className="text-xs" data-testid={`settlement-line-category-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="max-h-72">
                            {settlementCategoriesByBucket.map(group => (
                              <div key={group.bucket}>
                                <div className="text-[10px] uppercase font-semibold text-muted-foreground px-2 py-1">{group.label}</div>
                                {group.items.map(item => (
                                  <SelectItem key={item} value={item} className="text-xs">{item}</SelectItem>
                                ))}
                              </div>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-1">
                        <Select value={line.kind} onValueChange={v => updateLine(idx, { kind: v })}>
                          <SelectTrigger className="text-xs px-2" data-testid={`settlement-line-kind-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="charge" className="text-xs">Charge</SelectItem>
                            <SelectItem value="proration" className="text-xs">Proration</SelectItem>
                            <SelectItem value="credit" className="text-xs">Credit</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <Button size="icon" variant="ghost" onClick={() => removeLine(idx)} data-testid={`settlement-line-remove-${idx}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {lineItems.length > 0 && (
                <p className="text-[11px] text-muted-foreground mt-3 flex items-center gap-1.5">
                  <Wand2 className="w-3 h-3" />
                  Category auto-suggested from the description — change it any time.
                </p>
              )}
            </div>

            <Separator />

            {/* Summary */}
            <div className="bg-muted/40 rounded-sm p-4 space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Gross {side === 'buy' ? 'Contract' : 'Sale'} Price</span>
                <span className="font-mono">${fmt(grossPrice)}</span>
              </div>
              {side === 'buy' && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Less: Earnest Money Applied</span>
                  <span className="font-mono text-red-500">(${fmt(emdApplied)})</span>
                </div>
              )}
              {lineItems.filter(l => l.kind === 'charge').length > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{side === 'buy' ? 'Plus' : 'Less'}: Settlement Charges</span>
                  <span className="font-mono">{side === 'buy' ? '' : '('}${fmt(lineItems.filter(l => l.kind === 'charge').reduce((s, l) => s + Number(l.amount || 0), 0))}{side === 'buy' ? '' : ')'}</span>
                </div>
              )}
              {lineItems.filter(l => l.kind === 'proration').length > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{side === 'buy' ? 'Plus' : 'Less'}: Prorations</span>
                  <span className="font-mono">{side === 'buy' ? '' : '('}${fmt(lineItems.filter(l => l.kind === 'proration').reduce((s, l) => s + Number(l.amount || 0), 0))}{side === 'buy' ? '' : ')'}</span>
                </div>
              )}
              {lineItems.filter(l => l.kind === 'credit').length > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{side === 'buy' ? 'Less' : 'Plus'}: Credits</span>
                  <span className="font-mono">{side === 'buy' ? '(' : ''}${fmt(lineItems.filter(l => l.kind === 'credit').reduce((s, l) => s + Number(l.amount || 0), 0))}{side === 'buy' ? ')' : ''}</span>
                </div>
              )}
              <Separator className="my-2" />
              <div className="flex justify-between text-base font-bold">
                <span>Cash {side === 'buy' ? 'Wired at Closing' : 'Received at Closing'}</span>
                <span className="font-mono" data-testid="settlement-cash-at-closing">${fmt(cashAtClosing)}</span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                The project card will display the gross {side === 'buy' ? 'contract' : 'sale'} price (${fmt(grossPrice)}).
                Cash wired is informational only.
              </p>
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="settlement-notes" className="text-xs">Notes (optional)</Label>
              <textarea
                id="settlement-notes"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                className="w-full mt-1 border rounded-sm px-3 py-2 text-sm h-16 resize-none"
                placeholder="Any context that future-you should remember about this closing…"
                data-testid="settlement-notes"
              />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {statementId && (
            <Button variant="outline" onClick={handleDelete} disabled={saving} className="text-red-600 mr-auto" data-testid="settlement-delete-btn">
              Delete Statement
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || loading} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="settlement-save-btn">
            {saving ? 'Saving…' : (statementId ? 'Update Settlement' : 'Save Settlement')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
